import {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Plus, Trash2} from "lucide-react";
import {toast} from "react-toastify";

import {Button} from "@/components/ui/button";
import {ConfirmDialog} from "@/components/modal/ConfirmModal";

import {ClientTabs} from "@/components/clients/ClientsTabs";
import {ClientsTable} from "@/components/clients/ClientsTable";
import {ClientsFiltersBar} from "@/components/clients/ClientsFiltersBar";
import {Pagination} from "@/components/Pagintation";

import {usePeopleUI} from "@/hooks/usePeopleUI";

export default function ClientsPage() {
    const navigate = useNavigate();
    const ui = usePeopleUI();

    // bulk delete
    const [openBulkDelete, setOpenBulkDelete] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);

    // single delete
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [singleLoading, setSingleLoading] = useState(false);

    const handleAdd = () => navigate("/add-person");

    const bulkCount = ui.selectedIds.length;

    // Чтобы описание модалки было аккуратным
    const bulkDescription = useMemo(() => {
        if (!bulkCount) return "Выберите клиентов для удаления.";
        return `Будет удалено клиентов: ${bulkCount}. Это действие нельзя отменить.`;
    }, [bulkCount]);

    const handleConfirmBulkDelete = async () => {
        if (!ui.selectedIds.length) return;

        setBulkLoading(true);
        try {
            await ui.bulkDelete(ui.selectedIds);
            toast.success("Пользователи удалены");
            setOpenBulkDelete(false);
        } catch {
            toast.error("Ошибка при удалении");
        } finally {
            setBulkLoading(false);
        }
    };

    const handleConfirmSingleDelete = async () => {
        if (!deleteId) return;

        setSingleLoading(true);
        try {
            await ui.deletePersonById(deleteId);
            toast.success("Клиент удалён");
            setDeleteId(null);
        } catch {
            toast.error("Ошибка при удалении");
        } finally {
            setSingleLoading(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Клиенты</h1>
                    <p className="text-muted-foreground">База кредитованных клиентов</p>
                </div>

                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setOpenBulkDelete(true)}
                        disabled={bulkLoading || ui.selectedIds.length === 0}
                    >
                        <Trash2 className="h-4 w-4 mr-2"/>
                        Удалить
                    </Button>

                    <Button size="lg" onClick={handleAdd}>
                        <Plus className="h-4 w-4 mr-2"/>
                        Добавить клиента
                    </Button>
                </div>
            </div>

            <ClientsFiltersBar ui={ui}/>

            <ClientTabs activeTab={ui.tab} onTabChange={ui.actions.setTab}/>

            <ClientsTable
                people={ui.displayedPeople}
                selectedIds={ui.selectedIds}
                onSelect={ui.actions.toggleSelect}
                onSelectAll={ui.actions.selectAllOnPage}
                sortBy={ui.sortBy}
                sortDirection={ui.sortDirection}
                onSort={ui.actions.onSort}
                onView={(id) => navigate(`/detailInfo/${id}`)}
                onPrint={(id) => navigate(`/print/${id}`)}
                onDelete={async (id) => setDeleteId(id)}
            />

            <Pagination
                totalCount={ui.tabTotalCount}
                page={ui.page}
                pageSize={ui.pageSize}
                totalPages={ui.totalPages}
                onPageChange={ui.actions.setPage}
                onPageSizeChange={ui.actions.setPageSize}
                onPrevPage={ui.actions.prevPage}
                onNextPage={ui.actions.nextPage}
                getPageNumbers={ui.getPageNumbers}
            />

            {/* Моб кнопка добавить */}
            <div className="fixed bottom-4 left-4 right-4 md:hidden">
                <Button className="w-full" size="lg" onClick={handleAdd}>
                    <Plus className="h-4 w-4 mr-2"/>
                    Добавить
                </Button>
            </div>

            {/* ✅ Confirm: bulk delete */}
            <ConfirmDialog
                open={openBulkDelete}
                onOpenChange={setOpenBulkDelete}
                title="Удалить клиентов"
                description={bulkDescription}
                confirmText="Удалить"
                loading={bulkLoading}
                onConfirm={handleConfirmBulkDelete}
            />

            {/* ✅ Confirm: single delete */}
            <ConfirmDialog
                open={Boolean(deleteId)}
                onOpenChange={(open: any) => {
                    if (!open) setDeleteId(null);
                }}
                title="Удалить клиента"
                description="Клиент будет удалён навсегда. Это действие нельзя отменить."
                confirmText="Удалить"
                loading={singleLoading}
                onConfirm={handleConfirmSingleDelete}
            />
        </div>
    );
}
